import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { DEFAULT_LOCALE, INTL_LOCALE, Locale } from '@/enums/locale'
import { CLOSED_ROLES, TENANT_ROLES, TenantRole } from '@/enums/tenant'
import i18n, { isLocale } from '@/libs/i18n'
import type { IMe, IMeTenant } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const stored       = localStorage.getItem('locale')
    const locale       = ref<Locale>(isLocale(stored) ? stored : DEFAULT_LOCALE)
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IMe>()

    /*
     * Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — never a
     * second source of truth. Two modes only, 'light' | 'dark' — no System option in the UI
     * (vue-spec theme.md). Without `emitAuto` the ref always reads the RESOLVED mode, so a
     * first visit (nothing stored yet) shows whatever the OS prefers.
     */
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor (and
    // the mobile drawer) until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /*
     * What the person reading this may do (docs/API.md → Permissions). Every answer is false
     * until /me has come back — a screen that draws a button and takes it away a moment later is
     * worse than one that waits. The API enforces the same rules on every write; these only keep
     * somebody from being offered a button that would answer 403.
     *
     * A provider admin acts in ANY tenant with admin rights. A tenant user has one or more
     * memberships (`Me.tenants`), and in each tenant acts as the role of THAT membership: an
     * editor (channels, EPG, TV themes, content) or an admin (plus rooms, devices, TV stream
     * settings, the PMS link, the import and the tenant's users). So the
     * rights depend on the tenant being worked in — `currentTid`, the tenant of the address
     * (`/t/:tid/…`), which the router keeps up to date.
     */
    const currentTid = ref('')

    const setCurrentTid = (tid: string) => { currentTid.value = tid }

    const isProviderAdmin = () => userData.value?.provider_admin === true

    /** The person's own tenants (active only, sorted by name); [] for a provider admin. */
    const memberships = (): IMeTenant[] => userData.value?.tenants ?? []

    /** The person's membership in `tid`, if any. */
    const membershipIn = (tid: string): IMeTenant | undefined => memberships().find((item) => item.id === tid)

    /** May this person work in `tid` at all: a provider admin anywhere, a tenant user in their own. */
    const canOpenTenant = (tid: string) => isProviderAdmin() || !!membershipIn(tid)

    /** The person's first tenant (by name), or '' for a provider admin (and before /me). */
    const firstTenantId = () => memberships()[0]?.id ?? ''

    /** The role in `tid`: admin for a provider admin, the membership's role, else null. */
    const roleIn = (tid: string): string | null => {
        if (isProviderAdmin()) return TenantRole.Admin

        return membershipIn(tid)?.role ?? null
    }

    /** Admin rights in a tenant (the current one by default): rooms, devices, stream settings, PMS, import, users. */
    const isAdmin = (tid = currentTid.value) => roleIn(tid) === TenantRole.Admin

    /** Editor rights in a tenant (the current one by default): channels, EPG, themes, content. */
    const canEdit = (tid = currentTid.value) => {
        const role = roleIn(tid)

        return role === TenantRole.Admin || role === TenantRole.Editor
    }

    /** A kitchen user in a tenant: the room-service orders and the menu only (docs/API.md §13.2). */
    const isKitchen = (tid = currentTid.value) => !isProviderAdmin() && roleIn(tid) === TenantRole.Kitchen

    /** A housekeeping user in a tenant: the housekeeping requests only (docs/API.md §13.6). */
    const isHousekeeping = (tid = currentTid.value) => !isProviderAdmin() && roleIn(tid) === TenantRole.Housekeeping

    /**
     * The person's CLOSED role in a tenant (kitchen, housekeeping — `CLOSED_ROLES`), or null for
     * everybody who may open the console's other pages.
     */
    const closedRole = (tid = currentTid.value): TenantRole | null => {
        if (isProviderAdmin()) return null

        const role = roleIn(tid) as TenantRole | null

        return role && CLOSED_ROLES.includes(role) ? role : null
    }

    /** Guest feedback (docs/API.md §18.4): admins, editors and reception — never a closed role. */
    const handlesFeedback = (tid = currentTid.value) => {
        const role = roleIn(tid)

        return role === TenantRole.Admin || role === TenantRole.Editor || role === TenantRole.Reception
    }

    /** The reception actions (resets for the next guest, "Odhod danes"): editors, admins, reception. */
    const canReceive = (tid = currentTid.value) => canEdit(tid) || roleIn(tid) === TenantRole.Reception

    /** The occupancy chart's writes (docs/API.md §15.2): admins and reception — an editor only reads it. */
    const canBook = (tid = currentTid.value) => {
        const role = roleIn(tid)

        return role === TenantRole.Admin || role === TenantRole.Reception
    }

    /** A field technician in a tenant: the field view only (docs/API.md §16). */
    const isTechnician = (tid = currentTid.value) => !isProviderAdmin() && roleIn(tid) === TenantRole.Technician

    /** Pairing a screen and placing a device (name, room, on/off): admins and technicians (§16.1). */
    const canPair = (tid = currentTid.value) => isAdmin(tid) || isTechnician(tid)

    /*
     * The guest-requests inbox: every known role of the tenant but the technician (the kitchen sees
     * room service only, housekeeping its own requests only).
     */
    const canHandleRequests = (tid = currentTid.value) => TENANT_ROLES.includes(roleIn(tid) as TenantRole) && !isTechnician(tid)

    /** Every kind of request, not one kind only: everybody but a closed role. */
    const handlesBookings = (tid = currentTid.value) => canHandleRequests(tid) && !closedRole(tid)

    /*
     * A JWT cookie beside the header, so a file opened by <img src> or a link is authenticated too
     * — a browser cannot put an Authorization header on a link (docs/API.md: the API accepts
     * the `JWT` cookie as a fallback).
     */
    const setJWTCookie = (jwt: string) => {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''

        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax${secure}`
        } else {
            document.cookie = `JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secure}`
        }
    }

    // Restore the cookie on page load if the token is already in localStorage.
    setJWTCookie(JWT.value)

    /*
     * THE PERSON'S LANGUAGE WINS once they are known: `Me.locale` (docs/API.md) is theirs
     * wherever they sign in, and the choice made on the login page only lasts until then.
     */
    const changeLocale = (newLocale: string) => {
        locale.value = isLocale(newLocale) ? newLocale : DEFAULT_LOCALE
        localStorage.setItem('locale', locale.value)
        ;(i18n.global.locale as unknown as { value: string }).value = locale.value
        document.documentElement.lang = INTL_LOCALE[locale.value]
    }

    const setUserData = (data: IMe) => {
        userData.value = data

        if (isLocale(data.locale) && data.locale !== locale.value) changeLocale(data.locale)
    }

    const setLogin = (jwt: string, user?: IMe) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)

        if (user) setUserData(user)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value      = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    return {
        JWT, locale, userData, loaderActive, windowWidth, colorMode, currentTid,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        isProviderAdmin, memberships, membershipIn, canOpenTenant, firstTenantId, roleIn, isAdmin, canEdit, setCurrentTid,
        isKitchen, isHousekeeping, closedRole, handlesFeedback, isTechnician, canPair, canReceive, canBook, canHandleRequests, handlesBookings,
        setLogin, setLogout, setUserData, startLoading, stopLoading, changeLocale,
    }
})
